// Ses paketi detayı — /esim/voice/:bundleId
//
// Yapı UnlimitedDetail ile aynı (tekil bundle ucu olmadığı için liste
// önbellekten okunup id ile bulunur; ayrı plan detay ucu da yok, satın alma
// doğrudan checkout'a gider). Fark: veri miktarının yanında konuşma ve SMS
// hakları da gösterilir.
//
// DİKKAT: `isUnlimited` yalnızca VERİNİN sınırsız olduğunu söyler. Ses
// paketlerinde veri limitlidir (20/50/100 GB) ama konuşma & SMS sınırsızdır;
// bunlar voiceMinutes/smsCount alanlarından okunur (-1 = sınırsız). Veri
// etiketini asla "Unlimited Calls" başlığına bakarak koşullama.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faGlobe,
  faSpinner,
  faTriangleExclamation,
  faEarthAmericas,
  faBolt,
  faPhone,
  faCommentSms,
} from '@fortawesome/free-solid-svg-icons';
import StoreHeader from '../../components/StoreHeader';
import Footer from '../../components/Footer';
import AuthPromptModal from '../../components/AuthPromptModal';
import { useAuth } from '../../context/AuthContext';
import { formatDataOrUnlimited } from '../../utils/formatters';
import { findBundle, sortedPlans } from './bundleCatalog';
import { loadVoiceBundles, formatAllowance } from './voicePlans';
import VoicePlanList from './VoicePlanList';
import './home.css';

const Feature = ({ icon, label, value }) => (
  <div className="feature">
    <FontAwesomeIcon icon={icon} className="feature-icon" />
    <div>
      <span className="feature-label">{label}</span>
      <span className="feature-value">{value}</span>
    </div>
  </div>
);

const VoiceDetail = () => {
  const { t, i18n } = useTranslation();
  const { bundleId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [state, setState] = useState({ loading: true });
  const [authPromptOpen, setAuthPromptOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true });
    loadVoiceBundles()
      .then((list) => {
        if (cancelled) return;
        const bundle = findBundle(list, bundleId);
        setState(bundle ? { bundle } : { error: t('detail.empty') });
      })
      .catch((err) => !cancelled && setState({ error: err.message }));
    return () => {
      cancelled = true;
    };
    // Dil değişince katalog yeniden çekilir (ülke adları Accept-Language'e bağlı).
  }, [bundleId, i18n.language, t]);

  const bundle = state.bundle;
  const plans = sortedPlans(bundle);
  const countryNames = (bundle?.countries || [])
    .map((c) => c.nameSelected || c.name)
    .filter(Boolean);

  // Konuşma/SMS hakkı bundle genelinde aynı olduğu için ilk plandan okunur.
  const firstPlan = plans[0];
  const voiceLabel = formatAllowance(t, firstPlan?.voiceMinutes, 'voice');
  const smsLabel = formatAllowance(t, firstPlan?.smsCount, 'sms');

  const buy = (plan) => {
    if (!isAuthenticated) {
      setAuthPromptOpen(true);
      return;
    }
    navigate('/checkout', {
      state: {
        // plan.id hem ürün kimliği hem SKU'dur; backend `productId` olarak bunu
        // bekler. priceId fiyat kilidi — zorunlu.
        productId: String(plan.id),
        priceId: plan.priceId,
        plan: {
          countryName: bundle.title,
          dataAmount: formatDataOrUnlimited(t, plan.isUnlimited, plan.dataMb),
          validity: String(plan.validityDays),
          flag: bundle.imgUrl,
          // Tahsil edilen gerçek tutar versionedPriceEur'dur (PayPal, EUR).
          priceEuro: plan.versionedPriceEur || plan.priceEur,
        },
      },
    });
  };

  return (
    <div className="home">
      <StoreHeader />

      <section className="home-catalog detail">
        <Link to="/" className="esim-detail-back">
          <FontAwesomeIcon icon={faArrowLeft} />
          {t('home.detail.back')}
        </Link>

        <header className="detail-head">
          {bundle?.imgUrl ? (
            <img className="detail-flag" src={bundle.imgUrl} alt="" />
          ) : (
            <span className="detail-flag detail-flag--fallback">
              <FontAwesomeIcon icon={faGlobe} />
            </span>
          )}
          <div>
            <h1 className="detail-title">
              {bundle?.title || t('home.tabs.voice')}
              {bundle && <span className="detail-badge">{t('detail.voice')}</span>}
            </h1>
            {plans.length > 0 && (
              <p className="detail-sub">{t('detail.count', { count: plans.length })}</p>
            )}
          </div>
        </header>

        {state.loading && (
          <div className="home-state">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          </div>
        )}

        {state.error && (
          <div className="home-state">
            <FontAwesomeIcon icon={faTriangleExclamation} size="2x" />
            <p>{state.error}</p>
          </div>
        )}

        {bundle && (
          <>
            <p className="detail-notice">{t('detail.voiceNote')}</p>

            {plans.length === 0 ? (
              <div className="home-state">
                <p>{t('detail.empty')}</p>
              </div>
            ) : (
              <VoicePlanList plans={plans} onBuy={buy} />
            )}

            <h2 className="plan-section-title">{t('detail.basicFeatures')}</h2>
            <div className="plan-features">
              {voiceLabel && (
                <Feature icon={faPhone} label={t('detail.calls')} value={voiceLabel} />
              )}
              {smsLabel && (
                <Feature icon={faCommentSms} label={t('detail.sms')} value={smsLabel} />
              )}
              <Feature
                icon={faBolt}
                label={t('detail.activationMethod')}
                value={t('detail.esimActivation')}
              />
              {countryNames.length > 0 && (
                <Feature
                  icon={faEarthAmericas}
                  label={t('detail.coverageArea')}
                  value={countryNames.join(', ')}
                />
              )}
            </div>

            {bundle.description && (
              <>
                <h2 className="plan-section-title">{t('detail.description')}</h2>
                <p className="plan-description">{bundle.description}</p>
              </>
            )}
          </>
        )}
      </section>

      <Footer />

      <AuthPromptModal open={authPromptOpen} onClose={() => setAuthPromptOpen(false)} />
    </div>
  );
};

export default VoiceDetail;
